import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  firstValueFrom,
  map,
  Observable,
  retry,
  throwError,
  timeout,
  timer,
} from 'rxjs';
import {
  AccountingAccountCreateRequest,
  AccountingAccountDto,
  AccountingAccountFormSelects,
  AccountingAccountParentDto,
  AccountingAccountSelectOption,
  AccountingAccountTreeNodeDto,
  AccountingAccountType,
  AccountingAccountUpdateRequest,
  catalogWriteValue,
  readCatalogName,
  readCatalogUuid,
  resolveAccountingAccountNature,
  resolveAccountingAccountType,
} from './accounting-accounts-api.types';
import { mapSettingsError, SettingsApiError } from './settings-api.errors';
import { SETTINGS_API_URL } from './settings-api.tokens';

const REQUEST_TIMEOUT_MS = 15_000;
const ACCOUNTS_PATH = '/catalogs/accounting-accounts';

@Injectable({ providedIn: 'root' })
export class AccountingAccountsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${inject(SETTINGS_API_URL).replace(/\/+$/, '')}${ACCOUNTS_PATH}`;

  tree(search?: string): Observable<AccountingAccountTreeNodeDto[]> {
    let params = new HttpParams();
    const query = search?.trim();
    if (query) params = params.set('search', query);

    return this.http.get<unknown>(`${this.baseUrl}/tree`, { params }).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry({ count: 1, delay: error => this.retryDelay(error) }),
      map(body => unwrapTree(body)),
      catchError(error => throwError(() => mapAccountsError(error))),
    );
  }

  formSelects(): Promise<AccountingAccountFormSelects> {
    return this.request(
      this.http.get<unknown>(`${this.baseUrl}/form-selects`),
      body => unwrapFormSelects(body),
    );
  }

  getById(uuid: string): Promise<AccountingAccountDto> {
    return this.request(
      this.http.get<unknown>(`${this.baseUrl}/${uuid}`),
      body => unwrapItem(body),
    );
  }

  create(payload: AccountingAccountCreateRequest): Promise<AccountingAccountDto> {
    return this.request(
      this.http.post<unknown>(this.baseUrl, payload),
      body => unwrapItem(body),
    );
  }

  update(
    uuid: string,
    payload: AccountingAccountUpdateRequest,
  ): Promise<AccountingAccountDto> {
    return this.request(
      this.http.put<unknown>(`${this.baseUrl}/${uuid}`, payload),
      body => unwrapItem(body),
    );
  }

  remove(uuid: string): Promise<void> {
    return this.request(
      this.http.delete<unknown>(`${this.baseUrl}/${uuid}`),
      () => undefined,
    );
  }

  private async request<T>(
    source: Observable<unknown>,
    project: (body: unknown) => T,
  ): Promise<T> {
    try {
      const body = await firstValueFrom(source.pipe(timeout(REQUEST_TIMEOUT_MS)));
      return project(body);
    } catch (error) {
      throw mapAccountsError(error);
    }
  }

  private retryDelay(error: unknown): Observable<number> {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 429) {
        const retryAfter = Number(error.headers.get('Retry-After'));
        const seconds =
          Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : 1;
        return timer(seconds * 1000);
      }
      if (error.status >= 500 || error.status === 0) {
        return timer(300);
      }
    }
    return throwError(() => error);
  }
}

function mapAccountsError(error: unknown): SettingsApiError {
  const mapped = mapSettingsError(error);
  if (!(error instanceof HttpErrorResponse)) return mapped;
  const body = asRecord(error.error);
  if (!body) return mapped;
  const business = readString(body, 'error');
  const details = detailMessages(body['details']).filter(item => item !== business);
  if (business && details.length) {
    mapped.message = [business.replace(/\.$/, ''), ...details].join('. ');
  } else if (business) {
    mapped.message = business;
  } else if (details.length) {
    mapped.message = details.join('. ');
  }
  return mapped;
}

function detailMessages(details: unknown): string[] {
  const record = asRecord(details);
  if (!record) return [];
  const messages: string[] = [];
  for (const value of Object.values(record)) {
    if (typeof value === 'string' && value.trim()) messages.push(value.trim());
    if (!Array.isArray(value)) continue;
    for (const item of value) {
      if (typeof item === 'string' && item.trim()) messages.push(item.trim());
    }
  }
  return messages;
}

function unwrapData(body: unknown): unknown {
  if (body && typeof body === 'object' && 'data' in body) {
    return (body as { data: unknown }).data;
  }
  return body;
}

function unwrapTree(body: unknown): AccountingAccountTreeNodeDto[] {
  const data = unwrapData(body);
  if (!Array.isArray(data)) {
    throw new SettingsApiError('unexpected', 0);
  }
  return data.map(normalizeTreeNode);
}

function unwrapItem(body: unknown): AccountingAccountDto {
  const data = unwrapData(body);
  const item = normalizeAccount(data);
  if (!item) {
    throw new SettingsApiError('unexpected', 0);
  }
  return item;
}

function unwrapFormSelects(body: unknown): AccountingAccountFormSelects {
  const data = asRecord(unwrapData(body)) ?? asRecord(body);
  if (!data) throw new SettingsApiError('unexpected', 0);

  return {
    types: firstArray(data, ['types', 'type'])
      .map(toTypeSelectOption)
      .filter(isSelectOption),
    natures: firstArray(data, ['natures', 'nature'])
      .map(toNatureSelectOption)
      .filter(isSelectOption),
    titleParents: firstArray(data, ['titleParents', 'titleParent'])
      .map(normalizeParent)
      .filter((item): item is AccountingAccountParentDto => !!item),
    subtitleParents: firstArray(data, ['subtitleParents', 'subtitleParent'])
      .map(normalizeParent)
      .filter((item): item is AccountingAccountParentDto => !!item),
  };
}

function normalizeTreeNode(raw: unknown): AccountingAccountTreeNodeDto {
  const record = asRecord(raw);
  if (!record || typeof record['uuid'] !== 'string') {
    throw new SettingsApiError('unexpected', 0);
  }

  const type = resolveAccountingAccountType(record['type']);
  if (!type) {
    throw new SettingsApiError('unexpected', 0);
  }

  const childrenRaw = record['children'];
  return {
    uuid: record['uuid'],
    accountNumber: readString(record, 'accountNumber'),
    formattedAccountNumber: readString(record, 'formattedAccountNumber'),
    name: readString(record, 'name'),
    type,
    nature: resolveAccountingAccountNature(record['nature']),
    children: Array.isArray(childrenRaw) ? childrenRaw.map(normalizeTreeNode) : [],
  };
}

function normalizeAccount(raw: unknown): AccountingAccountDto | null {
  const record = asRecord(raw);
  if (!record || typeof record['uuid'] !== 'string') return null;

  const type = resolveAccountingAccountType(record['type']);
  if (!type) return null;

  return {
    uuid: record['uuid'],
    accountNumber: readString(record, 'accountNumber'),
    formattedAccountNumber: readString(record, 'formattedAccountNumber'),
    name: readString(record, 'name'),
    type,
    typeUuid: readCatalogUuid(record['type']),
    typeName: readCatalogName(record['type']),
    nature: resolveAccountingAccountNature(record['nature']),
    natureUuid: readCatalogUuid(record['nature']),
    natureName: readCatalogName(record['nature']),
    parent: normalizeParent(record['parent']),
    createdAt: optionalString(record, 'createdAt'),
    updatedAt: optionalString(record, 'updatedAt'),
  };
}

function normalizeParent(raw: unknown): AccountingAccountParentDto | null {
  const record = asRecord(raw);
  if (!record || typeof record['uuid'] !== 'string') return null;

  const type = resolveAccountingAccountType(record['type']);
  return {
    uuid: record['uuid'],
    accountNumber: readString(record, 'accountNumber'),
    formattedAccountNumber: readString(record, 'formattedAccountNumber'),
    name: readString(record, 'name'),
    type: type ?? ('TITLE' as AccountingAccountType),
  };
}

function toTypeSelectOption(raw: unknown): AccountingAccountSelectOption | null {
  const record = asRecord(raw);
  if (!record) return null;
  const type = resolveAccountingAccountType(raw);
  const name =
    typeof record['name'] === 'string'
      ? record['name']
      : typeof record['label'] === 'string'
        ? record['label']
        : type ?? '';
  const value = catalogWriteValue(raw, type);
  if (!type || !name || !value) return null;
  return { name, value, code: type };
}

function toNatureSelectOption(raw: unknown): AccountingAccountSelectOption | null {
  const record = asRecord(raw);
  if (!record) return null;
  const nature = resolveAccountingAccountNature(raw);
  const name =
    typeof record['name'] === 'string'
      ? record['name']
      : typeof record['label'] === 'string'
        ? record['label']
        : nature ?? '';
  const value = catalogWriteValue(raw, nature);
  if (!nature || !name || !value) return null;
  return { name, value, code: nature };
}

function isSelectOption(
  option: AccountingAccountSelectOption | null,
): option is AccountingAccountSelectOption {
  return option !== null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function firstArray(record: Record<string, unknown>, keys: string[]): unknown[] {
  for (const key of keys) {
    const value = record[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

function readString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value : '';
}

function optionalString(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];
  return typeof value === 'string' ? value : undefined;
}
